import type { ReactNode } from "react";
import { useMe } from "@/features/auth/useMe";
import { useAdminDiagnostics, useAdminStreams } from "../hooks/useAdminData";
import { hasAdminAccess } from "../nav";

/**
 * M8a F6 / F7 / F8 — always-visible KPI strip across the top of the
 * admin shell. Polls every 10 s via `useAdminDiagnostics()` +
 * `useAdminStreams()`; pauses on hidden tabs (the hook sets
 * `refetchIntervalInBackground: false`).
 *
 * For global admins the strip shows: Disk · Live · Viewers · Encoding
 *  · Failed · Matches · Blocks. For uploaders the strip is simplified
 * to Live + Role label only (legacy parity — uploaders don't need to
 * see encoder load or failed-slot counts).
 *
 * The strip mounts at the AdminShell level, NOT per sub-route, so
 * navigation between admin pages doesn't re-mount it or trigger
 * extra fetches.
 */
export function AdminStatusStrip() {
  const me = useMe().data;
  const diag = useAdminDiagnostics({
    enabled: !!me?.user?.is_global_admin,
  });
  const streams = useAdminStreams({
    enabled: !!me?.user?.is_global_admin,
  });

  if (!hasAdminAccess(me)) return null;

  // Non-global-admin strip — no diagnostics fetch (the hook is gated on
  // is_global_admin), so just show the role pill. Team admins reach this
  // surface via a `team_admin` membership; uploaders via the global
  // `uploader` role. Precedence matches `hasAdminAccess` / the shell
  // title in `nav.ts`: team_admin wins when both apply.
  if (!me?.user?.is_global_admin) {
    const isTeamAdmin = me?.memberships?.some((m) => m.role === "team_admin") ?? false;
    const roleLabel = isTeamAdmin ? "Team Admin" : "Uploader";
    return (
      <div
        data-testid="admin-status-strip"
        data-strip-role={isTeamAdmin ? "team_admin" : "uploader"}
        className="flex items-center gap-2 px-3 py-2 border-b border-rule bg-surface/40 text-xs font-mono"
      >
        <Pill testid="admin-strip-role" label="Role" value={roleLabel} tone="muted" />
      </div>
    );
  }

  const d = diag.data;
  const s = streams.data;
  const liveCount = s?.active?.filter((row) => row.kind === "live").length ?? 0;
  const vodCount = s?.active?.filter((row) => row.kind !== "live").length ?? 0;
  const blockCount = s?.blocks?.length ?? 0;
  const diskFreeBytes = d?.disk?.free ?? 0;
  const diskMinBytes = d?.disk?.min_free_bytes ?? 0;
  const diskOk = (d?.disk?.enough_space ?? true) && diskFreeBytes > diskMinBytes;
  const transcoding = d?.counts?.transcoding_slots ?? 0;
  const failed = d?.counts?.failed_slots ?? 0;

  return (
    <div
      data-testid="admin-status-strip"
      data-strip-role="admin"
      className="flex flex-wrap items-center gap-1.5 px-3 py-2 border-b border-rule bg-surface/40 text-xs font-mono"
    >
      <Pill
        testid="admin-strip-disk"
        label="Disk"
        value={diag.isLoading ? "…" : formatBytesShort(diskFreeBytes)}
        tone={diag.isLoading ? "muted" : diskOk ? "good" : "warn"}
      />
      <Pill
        testid="admin-strip-live"
        label="Live"
        value={liveCount > 0 ? "ON AIR" : "OFF"}
        tone={liveCount > 0 ? "accent" : "muted"}
      />
      <Pill
        testid="admin-strip-viewers"
        label="Viewers"
        value={streams.isLoading ? "…" : String(liveCount + vodCount)}
        tone="accent"
      />
      <Pill
        testid="admin-strip-encoding"
        label="Encoding"
        value={diag.isLoading ? "…" : String(transcoding)}
        tone={transcoding > 0 ? "busy" : "idle"}
      />
      <Pill
        testid="admin-strip-failed"
        label="Failed"
        value={diag.isLoading ? "…" : String(failed)}
        tone={failed > 0 ? "bad" : "good"}
      />
      <Pill
        testid="admin-strip-matches"
        label="Matches"
        value={diag.isLoading ? "…" : String(d?.counts?.matches ?? 0)}
        tone="muted"
      />
      {blockCount > 0 ? (
        <Pill
          testid="admin-strip-blocks"
          label="Blocks"
          value={String(blockCount)}
          tone="bad"
        />
      ) : null}
    </div>
  );
}

type PillTone = "good" | "warn" | "bad" | "busy" | "idle" | "accent" | "muted";

function Pill({
  label,
  value,
  tone,
  testid,
}: {
  label: string;
  value: ReactNode;
  tone: PillTone;
  testid: string;
}): ReactNode {
  const TONE_CLASSES: Record<PillTone, string> = {
    good: "border-good/40 text-good",
    // No `yellow` token in tokens.css; use accent for warning state.
    // The legacy strip's WARN state is rare (disk-low) — visually
    // distinct from BAD (red) is what matters; accent works.
    warn: "border-accent/40 text-accent",
    bad: "border-destructive/40 text-destructive",
    busy: "border-accent/40 text-accent",
    idle: "border-rule text-ink-muted",
    accent: "border-accent/40 text-accent",
    muted: "border-rule text-ink-muted",
  };
  return (
    <span
      data-testid={testid}
      data-tone={tone}
      className={`inline-flex items-center gap-1.5 rounded border bg-bg-card/40 px-2 py-0.5 ${TONE_CLASSES[tone]}`}
    >
      <span className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </span>
      <span className="text-xs">{value}</span>
    </span>
  );
}

/** "12.3 GB" / "2.0 MB" — small enough to fit in a status pill. */
function formatBytesShort(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return "0 B";
  const units = ["B", "KB", "MB", "GB", "TB"];
  let v = bytes;
  let i = 0;
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024;
    i += 1;
  }
  return `${v.toFixed(v < 10 ? 1 : 0)} ${units[i]}`;
}
